import { Form } from "components/form/form";
import { OptionHolder } from "components/input/select/options";
import { isTable, isTableRestrictionApplicable } from "components/table/table";
import { forEach } from "core-js/fn/array";
import { isActionButton } from "./actionButton/actionButton";
import { Bundle, isBundle } from "./Bundle";
import { ComponentFactory } from "./componentFactory";
import {
    Component,
    ComponentDefinition,
    DisplayedComponent,
    EmbedableComponentDefinition,
    isAutoValueComponent,
    isComponent,
    isComponentWithOptions,
    isDataHolderComponent,
    isFocusableComponent,
    isNestedComponentContainer,
    isParentAwareComponent,
    isParentComponent,
    isPostRenderedComponent,
    isRestrictableComponent,
    isValueComponent,
    ParentComponent,
    PostRenderedComponent,
} from "./components/Component";
import { FormRestrictions, RestrictionHolder, TableRestrictions } from "./restrictions/Restrictions";

export class Renderer {
    /** Cache of data objects embedded in a bundle.
     *  It will be emptied after the values are set to the components.
     */
    private data = new WeakMap();
    private options = new WeakMap();
    private restrictions = new WeakMap();
    private static renderedComponents = new WeakMap();
    private focus: string;

    public build(componentDefinition: ComponentDefinition): Component {
        // Create the component, this could be moved to the factory?
        let module = ComponentFactory.get(componentDefinition.componentType);
        if (module == null) {
            console.error("No module definition found for " + componentDefinition.componentType);
            return;
        }

        let component = new module.prototype.constructor(componentDefinition);

        if (isParentComponent(component)) {
            if (component.definition.children != null) {
                component.definition.children.forEach((val) => {
                    let child;
                    if (isComponent(val)) {
                        child = this.build(val);
                    }
                    if (isBundle(val)) {
                        child = this.build(val.definition);
                        if (val.data != null) {
                            this.data.set(child.definition, val.data);
                        }
                        if (val.options != null) {
                            this.options.set(child.definition, val.options);
                        }
                        if (val.restrictions != null) {
                            this.restrictions.set(child.definition, val.restrictions);
                        }
                        if (val.focus != null) {
                            this.focus = val.focus;
                        }
                    }
                    (component as ParentComponent).children.push(child);
                });
            }
        }

        return component;
    }

    public render(component: Component): Node {
        let rendered = component.render();

        if (isParentComponent(component)) {
            component.children.forEach((child) => {
                let r = this.render(child);
                component.appendChildView(r);
            });
        }
        if (rendered != null) {
            Renderer.renderedComponents.set(rendered, component);
        }
        return rendered;
    }

    public fullComponentRender(definition: ComponentDefinition, host: Element): Promise<Component> {
        return this.fullRender(Bundle.create(definition), host);
    }

    public async asyncFullComponentRender(definition: ComponentDefinition, host: Element): Promise<Component> {
        return await this.fullRender(Bundle.create(definition), host);
    }

    public fullBundleReplace(bundle: Bundle, target: DisplayedComponent | Element): Promise<Component> {
        if (target instanceof Element) {
            return this.fullRender(bundle, target, null, true);
        } else {
            return this.fullRender(bundle, target.view, null, true);
        }
    }

    public async fullRender(bundle: Bundle, host: Element, parent?: Component, replace?: boolean, focus?: string): Promise<Component> {
        // Load the host to load after we rendered the bundle.
        // We do it to prefetch the container if needed and thus to speed the load time
        host = await this.loadHosted(bundle, host);
        let component = this.build(bundle.definition);
        if (isParentAwareComponent(component)) {
            component.parent = parent;
        }
        // Render
        let rendered = this.render(component);

        // Apply options
        this.iterateToAddOptions(component, bundle.options);
        // Apply restrictions
        this.iterateToSetRestrictions(component, bundle.restrictions);

        // Apply Values
        this.iterateToSetData(component, bundle.data);

        // Add to host
        if (host != null && rendered != null) {
            host.innerHTML = "";
            if (replace) {
                host.replaceWith(rendered);
            } else {
                host.appendChild(rendered);
            }
        }

        // post render
        Renderer.iterateComponentTree(
            component,
            (c) => isPostRenderedComponent(c),
            (c) => (c as PostRenderedComponent).postRender()
        );

        this.iterateToSetFocus(component, ((focus != null) ? focus : this.focus));

        return component;
    }

    private async loadHosted(bundle: Bundle, defaultHost: Element): Promise<Element> {
        let hosted = (bundle.definition as EmbedableComponentDefinition);
        if (hosted.host != null) {
            let host = document.querySelector((bundle.definition as EmbedableComponentDefinition).host);
            if (host == null) {
                let response = await fetch(hosted.container);
                let data = await response.json();
                await this.fullComponentRender(data, document.querySelector("body"));
                host = document.querySelector((bundle.definition as EmbedableComponentDefinition).host);
            }
            return host;
        }
        return defaultHost;
    }

    public static iterateComponentTree(
        component: Component,
        filter: (c: Component) => boolean,
        callback: (c: Component) => void,
        reverseCallback: (c: Component) => void = () => { },
        reverseFilter: (c: Component) => boolean = filter,
    ) {
        if (filter(component)) {
            callback(component);
        }
        if (isParentComponent(component)) {
            for (let child of component.children) {
                this.iterateComponentTree(child, filter, callback, reverseCallback, reverseFilter);
            }
        }
        if (reverseFilter(component)) {
            reverseCallback(component);
        }
    }

    public static findFirstInTree(component: Component, filter: (c: Component) => boolean): Component {
        if (filter(component)) {
            return component;
        }
        if (isParentComponent(component)) {
            let found;
            for (let child of component.children) {
                found = this.findFirstInTree(child, filter);
                if (found != null) {
                    return found;
                }
            }
        }
        if (isNestedComponentContainer(component)) {
            for (const c of component.getNestedComponents()) {
                let found;
                found = this.findFirstInTree(c, filter);
                if (found != null) {
                    return found;
                }
            }
        }
        return null;
    }

    public iterateToAddOptions(component: Component, options: OptionHolder): void {
        let mode = "leaf";
        if (isParentComponent(component)) {
            mode = "tree";
        }
        Renderer.iterateComponentTree(
            component,
            (c) => isComponentWithOptions(c) || isBundle(c),
            (c) => {
                if (isComponentWithOptions(c)) {
                    if (options != null) {
                        if (mode === "leaf") {
                            c.addOptions(options);
                            return;
                        } else {
                            c.addOptions(options[c.definition.property]);
                        }
                    }
                }
                if (this.options.has(c.definition)) {
                    /* This is a bundle */
                    let componentOptions = this.options.get(c.definition);
                    this.options.delete(c.definition);
                    this.iterateToAddOptions(c, componentOptions);
                    return;
                }
            },
        );
    }

    public iterateToSetOptions(component: Component, options: OptionHolder): void {
        let mode = "leaf";
        if (isParentComponent(component)) {
            mode = "tree";
        }
        Renderer.iterateComponentTree(
            component,
            (c) => isComponentWithOptions(c) || isBundle(c),
            (c) => {
                if (isComponentWithOptions(c)) {
                    if (options != null) {
                        if (mode === "leaf") {
                            c.setOptions(options);
                            return;
                        } else if (options[c.definition.property] !== undefined) {
                            c.setOptions(options[c.definition.property]);
                        }
                    }
                }
                if (this.options.has(c.definition)) {
                    /* This is a bundle */
                    let componentOptions = this.options.get(c.definition);
                    this.options.delete(c.definition);
                    this.iterateToSetOptions(c, componentOptions);
                    return;
                }
            },
        );
    }

    public iterateToSetRestrictions(component: Component, restrictions: RestrictionHolder): void {
        if (restrictions == null) {
            this.iterateToSetRestrictionsOnObject(component, null);
        } else {
            if (component instanceof Form) {
                component.initRestrictions(restrictions as FormRestrictions);
            }
            if (isTableRestrictionApplicable(component)) {
                component.applyRestrictions(restrictions as TableRestrictions);
            }
            this.iterateToSetRestrictionsOnObject(component, restrictions);
        }
    }

    private iterateToSetRestrictionsOnObject(component: Component, restrictions: RestrictionHolder) {
        if (this.restrictions.has(component.definition)) {
            let componentRestrictions = this.restrictions.get(component.definition);
            this.restrictions.delete(component.definition);
            this.iterateToSetRestrictions(component, componentRestrictions);
        } else {
            if (isRestrictableComponent(component) && restrictions != null) {
                let restrictionsForComponent = restrictions[component.definition["property"] || component.definition["id"]];
                if (restrictionsForComponent != null) {
                    component.applyRestrictions(restrictionsForComponent);
                }
            }

            if (isParentComponent(component)) {
                if (component instanceof Form) {
                    component.initRestrictions(restrictions as FormRestrictions);
                }
                for (let child of component.children) {
                    // TODO subbundles
                    this.iterateToSetRestrictionsOnObject(child, restrictions);
                }
            }
        }
    }

    /** For partial data updates. */
    public iterateToUpdateData(component: Component, data) {
        let mode = "leaf";
        if (isParentComponent(component)) {
            mode = "tree";
        }
        let dataStack = [];
        Renderer.iterateComponentTree(
            component,
            () => true,
            (c) => {
                if (isValueComponent(c)) {
                    if (data != null) {
                        if (mode === "leaf") {
                            if (isDataHolderComponent(c)) {
                                c.updateData(data);
                            } else {
                                c.value(data);
                            }
                            return;
                        } else {
                            if (c.definition.property !== undefined && data[c.definition.property] !== undefined) {
                                if (isDataHolderComponent(c)) {
                                    c.updateData(data[c.definition.property]);
                                } else {
                                    c.value(data[c.definition.property]);
                                }
                            }
                        }
                    }
                    if (isAutoValueComponent(c)) {
                        if (data == null) {
                            c.autoInitValue();
                        }
                        return;
                    }
                }
                if (isKeepsOriginalData(c)) {
                    dataStack.push(data);
                }
            },
            (c) => {
                if (isKeepsOriginalData(c)) {
                    c.updateData(dataStack.pop());
                }
            },
        );
    }

    public iterateToSetData(component: Component, data) {
        let mode = "leaf";
        if (isParentComponent(component)) {
            mode = "tree";
        }
        let bundledData;
        let dataStack = [];
        Renderer.iterateComponentTree(
            component,
            () => true,
            (c) => {
                if (bundledData != null) {
                    data = bundledData;
                    bundledData = null;
                }
                if (isValueComponent(c)) {
                    if (data != null) {
                        if (mode === "leaf") {
                            c.value(data);
                            return;
                        } else if (data[c.definition.property] !== undefined) {
                            c.value(data[c.definition.property]);
                        } else {
                            c.value(null);
                        }
                    }
                }
                if (this.data.has(c.definition)) {
                    /* This is a bundle */
                    bundledData = this.data.get(c.definition);
                    this.data.delete(c.definition);

                    if (isValueComponent(c)) {
                        c.value(bundledData);
                        bundledData = null;
                        return;
                    }
                    // this.iterateToAddData(c, componentData);
                }
                if (isAutoValueComponent(c)) {
                    if (data == null) {
                        c.autoInitValue();
                    }
                    return;
                }
                if (isKeepsOriginalData(c)) {
                    dataStack.push(data || bundledData);
                }
            },
            (c) => {
                if (isKeepsOriginalData(c)) {
                    c.updateData(dataStack.pop());
                }
            },
        );
    }

    public iterateToSetFocus(component: Component, focus: string) {
        if (focus == null) {
            return;
        }
        const focusComponent = Renderer.findFirstInTree(
            component,
            (c) => {
                return isValueComponent(c) && c.definition.property === focus
                    || isActionButton(c) && c.definition.action.actionName === focus;
            });
        if (focusComponent == null) {
            console.error("Component " + focus + " must be focused but hasn't been found.");
        } else if (isFocusableComponent(focusComponent)) {
            focusComponent.focus();
        } else {
            focusComponent["view"].querySelector("button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])").focus();
        }
    }

    public static instance(): Renderer {
        return new Renderer();
    }

    public static getComponentForView(view: Node): Component {
        return Renderer.renderedComponents.get(view);
    }
}

interface KeepsOriginalData {
    originalData: any;
    currentData: any;
    updateData(data: any): void;
}

function isKeepsOriginalData(arg: any): arg is KeepsOriginalData {
    return arg.originalData !== undefined && arg.updateData !== undefined;
}
